import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { setToken } from "@/lib/api";
import { queueCount } from "@/lib/secureStore";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const { t, setLang } = useI18n();
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=auth

  const refresh = useCallback(async (tok) => {
    try {
      const { data } = await api.get("/auth/me", tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined);
      setUser(data);
      if (data?.lang && data.lang !== localStorage.getItem("bvd_lang")) setLang(data.lang, { persist: false });
      return data;
    } catch (e) {
      // 403 = action de sécurité requise (changement de mot de passe) : l'utilisateur est bien connecté
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 403 && d?.code) {
        setUser({ security_gate: d.code, must_change_password: true });
        return null;
      }
      setUser(false);
      return null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setUser((u) => {
        if (u) toast.error(t("session_expired"));
        return false;
      });
    };
    window.addEventListener("bvd:session-expired", onExpired);
    return () => window.removeEventListener("bvd:session-expired", onExpired);
  }, [t]);

  const login = useCallback(async (email, password, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    if (data.mfa_required) return data; // un code e-mail est requis
    if (data.token) setToken(data.token);
    const me = await refresh(data.token);
    if (!me) { setUser(data.user || false); }
    return data;
  }, [refresh]);

  const verifyLoginCode = useCallback(async (challenge, code) => {
    const { data } = await api.post("/auth/login/verify", { challenge, code });
    if (data.token) setToken(data.token);
    const me = await refresh(data.token);
    if (!me) { setUser(data.user || false); }
    return data;
  }, [refresh]);

  const resendLoginCode = useCallback(async (challenge) => {
    const { data } = await api.post("/auth/login/resend", { challenge });
    return data;
  }, []);

  const logout = useCallback(async (opts = {}) => {
    if (!opts.force) {
      try {
        const n = await queueCount();
        if (n > 0 && !window.confirm(`${n} saisie(s) non synchronisée(s) restent chiffrées sur cet appareil et ne seront pas envoyées. Se déconnecter quand même ?`)) return false;
      } catch { /* ignore */ }
    }
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
    return true;
  }, []);

  // Déconnexion automatique après 30 min d'inactivité
  useEffect(() => {
    if (!user) return undefined;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setToken(null);
        setUser(false);
        toast.error(t("session_expired_30"));
      }, 30 * 60 * 1000);
    };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => { clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Beacon terrain : rapporte le nombre de saisies hors-ligne en attente (aucune donnée), par appareil
  useEffect(() => {
    if (!user) return undefined;
    let id;
    const send = async () => {
      try {
        let did = localStorage.getItem("bvd_device_id");
        if (!did) { did = (window.crypto?.randomUUID?.() || String(Date.now())); localStorage.setItem("bvd_device_id", did); }
        const pending = await queueCount();
        await api.post("/devices/heartbeat", { device_id: did, pending, online: navigator.onLine });
      } catch { /* ignore */ }
    };
    send();
    id = setInterval(send, 60000);
    const on = () => send();
    window.addEventListener("online", on);
    window.addEventListener("offline", on);
    return () => { clearInterval(id); window.removeEventListener("online", on); window.removeEventListener("offline", on); };
  }, [user]);

  return <AuthCtx.Provider value={{ user, login, verifyLoginCode, resendLoginCode, logout, refresh }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
